import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { PortfolioPagesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { PortfolioPageFilters, PortfolioPageOrder, SortOrder } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { EditDialog } from './edit_dialog'
import { NewDialog } from './new_dialog'
import { Badge } from '../base/badge'

interface Props extends TableProps {
  createable?: boolean
  updateable?: boolean
  filters?: PortfolioPageFilters
}

export const Table = ({ pageSize = 10, filters, createable = false, updateable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<PortfolioPagesQuery>(
    Query,
    { variables: { filters: filters || {}, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openEdit, setOpenEdit } = useTable({ pageInfo: data?.portfolioPages.pageInfo })
  const [portfolioPageId, setPortfolioPageId] = useState<string>()

  const editPortfolioPage = (id: string) => {
    if (!updateable) return

    setPortfolioPageId(id)
    setOpenEdit(true)
  }

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  return (
    <>
      <BaseTable<PortfolioPageFilters, PortfolioPageOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ title: SortOrder.Asc }}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.portfolioPages.nodes = dedupe([
              ...previousData.portfolioPages.nodes,
              ...fetchMoreResult.portfolioPages.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, titleCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
          {
            index: 2,
            setValue: (set, value) => set(f => ({ ...f, slugCont: value })),
            type: 'text',
            placeholder: 'Or where did we link to it?'
          }
        ]}
        headings={[
          { name: 'Title' },
          { name: 'Description' },
          { name: 'Slug' },
          { name: 'Event' },
          { name: 'Enabled' },
          { name: 'Link' },
        ]}
        rows={data.portfolioPages.nodes.map(portfolioPage => (
          {
            id: portfolioPage.id,
            columns: [
              portfolioPage.title,
              portfolioPage.description,
              portfolioPage.slug,
              portfolioPage.event.description,
              <Badge color={portfolioPage.enabled ? 'green' : 'red'}>
                { portfolioPage.enabled ? 'Yes' : 'No' }
              </Badge>,
              portfolioPage.publicLink ? <a href={portfolioPage.publicLink} target="_blank">View</a> : <></>
            ]
          }
        ))}
        onRowClick={editPortfolioPage}
      />
      { portfolioPageId && <EditDialog id={portfolioPageId} open={openEdit} setOpen={setOpenEdit} /> }
      <NewDialog
        defaultValues={{ eventId: filters?.eventIdEq || undefined }}
        open={openNew}
        setOpen={setOpenNew}
      />
    </>
  )
}
